import { NavLink, Outlet, useParams } from "react-router";
import { useVbsOrderShowQuery } from "@/services/backend/vbs/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Order } from "@/services/backend/vbs/orders/types/order";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteOrderDialog } from "@/routes/processes/[processId]/vbs/orders/[orderId]/_components/delete-order-dialog";
import { ErrDocumentNotReady } from "@bitory-gmbh/bsm";
import {
  ErrDocumentNotReadyContextProvider,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import t from "@/lang/lang";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { VbsBreadcrumbs } from "@/routes/processes/[processId]/vbs/_components/vbs-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function OrderRoute() {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading,
    error,
  } = useVbsOrderShowQuery({
    id: orderId!,
  });

  const breadcrumbs = order && (
    <VbsBreadcrumbs customerId={order.customerId} processId={order.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/vbs/${order.processId!}/orders`}>
            {t("Auftragseingänge")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={order.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </VbsBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {order && <Page order={order} />}
    </ProcessPageMainTemplate>
  );
}

export function Page({ order }: { order: Order }) {
  return (
    <PersistenceContextProvider>
      <ErrDocumentNotReadyContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <TabNavigation />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteOrderDialog orderId={order.id} />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <Outlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrDocumentNotReadyContextProvider>
    </PersistenceContextProvider>
  );
}

function TabNavigation() {
  const { notReadyError } = useErrDocumentNotReady();

  const links = [
    {
      to: "data",
      label: t("Auftragseingang"),
      hasValidationError: hasOrderError(notReadyError),
    },
    { to: "treatment", label: t("Leistungen") },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      hasValidationError:
        hasAddressError(notReadyError) || hasRepresentativeError(notReadyError),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          hasValidationError={link.hasValidationError}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}

function hasOrderError(error: ErrDocumentNotReady | null): boolean {
  if (!error) {
    return false;
  }

  const fields = [
    "date",
    "treatment.offerPrice",
    "pricesForExtras.ratePerAccessPanel",
    "pricesForExtras.hourlyRate",
  ];

  return fields.some((field) => field in error.errors);
}

function hasAddressError(error: ErrDocumentNotReady | null): boolean {
  if (!error) {
    return false;
  }

  const fields = [
    "serviceAddress.company",
    "serviceAddress.street",
    "serviceAddress.postalCode",
    "serviceAddress.city",
    "customerAddress.company",
    "customerAddress.street",
    "customerAddress.postalCode",
    "customerAddress.city",
  ];

  return fields.some((field) => field in error.errors);
}

function hasRepresentativeError(error: ErrDocumentNotReady | null): boolean {
  if (!error) {
    return false;
  }

  const fields = [
    "representative.email",
    "representative.mobilephone",
    "representative.name",
  ];

  return fields.some((field) => field in error.errors);
}
